import { posthog } from "~/lib/posthog";
import { normalizeContentRewardJoinAttemptId } from "./content-reward-join-observability";

export interface RewardRegistrationContext {
  rewardId: string;
  joinAttemptId: string;
}

/** Telemetry only. Supports anonymous sign-in and nested recovery destinations.
 * Never use these caller-supplied IDs as identity or mutation authority. */
export function getRewardRegistrationContext(value: string | null | undefined): RewardRegistrationContext | null {
  for (let depth = 0; depth < 4 && value; depth++) {
    if (value.length > 8192 || !value.startsWith("/") || value.startsWith("//")) return null;
    try {
      const url = new URL(value, "https://local.invalid");
      if (url.origin !== "https://local.invalid") return null;
      const match = url.pathname.match(/^\/creator_studio\/content_rewards\/browse\/([^/]+)$/);
      if (match) {
        const rewardId = normalizeContentRewardJoinAttemptId(match[1]);
        const joinAttemptId = normalizeContentRewardJoinAttemptId(url.searchParams.get("join_attempt_id"));
        return rewardId && joinAttemptId ? { rewardId, joinAttemptId } : null;
      }
      if (!["/account-link/recovery", "/account-link/complete"].includes(url.pathname)) return null;
      value = url.searchParams.get("returnTo");
    } catch { return null; }
  }
  return null;
}

export function rewardAuthFetchOptions(context: RewardRegistrationContext | null) {
  return context ? { headers: {
    "x-sfn-reward-id": context.rewardId,
    "x-sfn-join-attempt-id": context.joinAttemptId,
  } } : undefined;
}

export type RewardAuthMode = "signin" | "signup" | "forgot";
export function trackRewardAuthForm(
  context: RewardRegistrationContext | null,
  mode: RewardAuthMode,
  step: "shown" | "mode_changed" | "interaction" | "validation_blocked" | "submitted",
) {
  if (!context) return;
  posthog.capture("content_reward_join_auth_form", {
    reward_id: context.rewardId,
    join_attempt_id: context.joinAttemptId,
    auth_mode: mode,
    form_step: step,
  }, { send_instantly: true });
}
